// 3-stage flashcard. Rotates on X-axis between stages using framer-motion.
// Content per stage depends on word type and study mode (DE->TR or TR->DE).
// When the word is a "stub" (no Turkish translation yet), shows a loading
// placeholder for stages 1 and 2 while AI enrichment is in flight.

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowsClockwise, Spinner } from '@phosphor-icons/react';
import { STUDY } from '../constants/testIds';
import { isStub } from '../lib/store';

const ARTICLE_COLOR = {
  der: 'text-blue-600 dark:text-blue-400',
  die: 'text-rose-600 dark:text-rose-400',
  das: 'text-emerald-600 dark:text-emerald-400',
};

function TypeBadge({ type }) {
  const label = {
    noun: 'NOMEN',
    adjective: 'ADJEKTIV',
    adverb: 'ADVERB · PRÄPOSITION',
    verb: 'VERB',
  }[type] || type.toUpperCase();
  return (
    <span className="overline border border-current px-2 py-1">{label}</span>
  );
}

// ----- Per-stage content ---------------------------------------------------
function NounStages({ word, mode }) {
  const { german, turkish, article, plural, sentence } = word;
  if (mode === 'de-tr') {
    return [
      <BigWord key="s0" text={german} />,
      <BigWord key="s1" text={turkish} subdued />,
      <DetailStack key="s2">
        <div className="flex items-baseline gap-3">
          <span className={`font-display font-black text-4xl ${ARTICLE_COLOR[article] || ''}`}>{article}</span>
          <span className="font-display font-black text-4xl">{german}</span>
        </div>
        <Row label="PLURAL" value={plural} />
        <Row label="B2 SATZ" value={sentence} italic />
      </DetailStack>,
    ];
  }
  // TR -> DE
  return [
    <BigWord key="s0" text={turkish} />,
    <div key="s1" className="flex items-baseline gap-4">
      <span className={`font-display font-black text-5xl sm:text-6xl ${ARTICLE_COLOR[article] || ''}`}>{article}</span>
      <span className="font-display font-black text-5xl sm:text-6xl tracking-tighter">{german}</span>
    </div>,
    <DetailStack key="s2">
      <Row label="PLURAL" value={plural} />
      <Row label="B2 SATZ" value={sentence} italic />
    </DetailStack>,
  ];
}

function AdjAdvStages({ word, mode }) {
  const { german, turkish, sentences = [] } = word;
  const front = mode === 'de-tr' ? german : turkish;
  const middle = mode === 'de-tr' ? turkish : german;
  return [
    <BigWord key="s0" text={front} />,
    <BigWord key="s1" text={middle} subdued />,
    <DetailStack key="s2">
      <div className="overline mb-1">B2 BEISPIELE</div>
      <ol className="space-y-3">
        {sentences.map((s, i) => (
          <li key={`${i}-${s.slice(0, 24)}`} className="flex gap-3 text-lg sm:text-xl leading-snug">
            <span className="font-mono-plex text-primary font-bold w-6 flex-none">{i + 1}.</span>
            <span className="italic">{s}</span>
          </li>
        ))}
      </ol>
    </DetailStack>,
  ];
}

function VerbStages({ word, mode }) {
  const { german, turkish, praesens, praeteritum, perfekt } = word;
  const front = mode === 'de-tr' ? german : turkish;
  const middle = mode === 'de-tr' ? turkish : german;
  return [
    <BigWord key="s0" text={front} />,
    <BigWord key="s1" text={middle} subdued />,
    <DetailStack key="s2">
      <Row label="PRÄSENS" value={praesens} mono />
      <Row label="PRÄTERITUM" value={praeteritum} mono />
      <Row label="PERFEKT" value={perfekt} mono />
    </DetailStack>,
  ];
}

// ----- Atomic pieces -------------------------------------------------------
function BigWord({ text, subdued }) {
  return (
    <h2
      className={`font-display font-black tracking-tighter leading-[0.95] ${
        subdued
          ? 'text-4xl sm:text-5xl text-muted-foreground'
          : 'text-5xl sm:text-7xl'
      }`}
    >
      {text}
    </h2>
  );
}

function Row({ label, value, italic, mono }) {
  return (
    <div className="border-t border-border pt-3">
      <div className="overline mb-1">{label}</div>
      <div className={`text-lg sm:text-xl ${italic ? 'italic' : ''} ${mono ? 'font-mono-plex' : ''}`}>
        {value}
      </div>
    </div>
  );
}

function DetailStack({ children }) {
  return <div className="w-full space-y-4">{children}</div>;
}

// ----- Stage resolver ------------------------------------------------------
function getStages(word, mode) {
  if (isStub(word)) {
    // The bundled card hasn't been enriched yet — show loading placeholders
    const front = mode === 'de-tr' ? word.german : '…';
    return [
      <BigWord key="s0" text={front} />,
      <LoadingStage key="s1" />,
      <LoadingStage key="s2" />,
    ];
  }
  switch (word.type) {
    case 'noun': return NounStages({ word, mode });
    case 'adjective': return AdjAdvStages({ word, mode });
    case 'adverb': return AdjAdvStages({ word, mode });
    case 'verb': return VerbStages({ word, mode });
    default: return AdjAdvStages({ word, mode });
  }
}

function LoadingStage() {
  return (
    <div className="flex items-center gap-3 text-muted-foreground">
      <Spinner size={28} weight="bold" className="animate-spin" />
      <div>
        <div className="overline mb-2">YAPAY ZEKÂ ÇALIŞIYOR</div>
        <div className="font-display text-2xl tracking-tight">
          Detaylar yükleniyor…
        </div>
      </div>
    </div>
  );
}

const STAGE_LABEL = ['1 / 3 · Wort', '2 / 3 · Bedeutung', '3 / 3 · Detail'];

export default function Flashcard({ word, mode, stage, onFlip }) {
  const stages = getStages(word, mode);
  const node = stages[stage];

  return (
    <div className="card-3d-wrap">
      <AnimatePresence mode="wait">
        <motion.div
          key={`${word.id}-${stage}`}
          initial={{ rotateX: -90, opacity: 0 }}
          animate={{ rotateX: 0, opacity: 1 }}
          exit={{ rotateX: 90, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 220, damping: 22 }}
          className="origin-top will-change-transform"
        >
          <button
            data-testid={STUDY.card}
            onClick={onFlip}
            className="w-full text-left bg-card border border-border p-8 sm:p-14 min-h-[440px] flex flex-col justify-between rounded-sm hover:border-foreground/40 transition-colors"
          >
            <div className="flex items-center justify-between">
              <TypeBadge type={word.type} />
              <span className="font-mono-plex overline">{STAGE_LABEL[stage]}</span>
            </div>

            <div className="py-6 sm:py-10">
              {node}
            </div>

            <div className="flex items-center justify-between border-t border-border pt-4 text-xs text-muted-foreground">
              <span className="font-mono-plex tracking-[0.25em]">KLICK · UM · WEITERZUGEHEN</span>
              <span className="flex items-center gap-1">
                {stage < 2
                  ? <>İleri <ArrowRight size={14} weight="bold" /></>
                  : <>Yeniden <ArrowsClockwise size={14} weight="bold" /></>}
              </span>
            </div>
          </button>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
